<script setup lang="ts">
import type { PaginationDto } from "core/dto";
import { LOCALE } from "core/interfaces";

import { getKindPathMapping } from "~/common/helpers/get-kind-path-mapping";
import { navigateToItemDetails } from "~/common/helpers/navigate-to-item-details";
import type { GetAllShopProductsDto } from "~/common/interfaces/dto/shop/get-all-shop-products.dto";
import type { ShopComponentsResponseDto } from "~/common/interfaces/dto/shop/shop-components-response.dto";
import type { ShopProductsResponseDto } from "~/common/interfaces/dto/shop/shop-products-response.dto";
import type { UpdateSearch } from "~/common/interfaces/update-search.interface";

const { locale } = useI18n();

const props = defineProps({
	components: {
		type: Object as PropType<ShopComponentsResponseDto>,
		default: () => {
			return {};
		}
	},
	products: {
		type: Array as PropType<ShopProductsResponseDto[]>,
		default () {
			return [];
		}
	},
	productsTotal: {
		type: Number,
		default: 0,
		required: true
	}
});
const emit = defineEmits<{(event: "update:page", dto: GetAllShopProductsDto, paginationDto: Partial<PaginationDto>, mergeProducts: boolean): void}>();

const updatePage = (updateSearch: UpdateSearch) => {
	emit("update:page", {}, { page: updateSearch.page }, updateSearch.showMore);
};

const navigateToItemDetailsBound = navigateToItemDetails.bind(this, locale.value as LOCALE, props.components.kinds);

const kindPathMapping = computed(() => getKindPathMapping(locale.value as LOCALE, props.components.kinds));
</script>

<template>
	<section>
		<AppBreadcrumbs
			:replace-params="false"
			:renamed-paths="{
				shop: $t('shop'),
				...kindPathMapping
			}"
		/>
		<div
			class="mt-7 mb-7 grid grid-cols-[repeat(auto-fill,minmax(256px,1fr))] gap-x-4 gap-y-6 lg:gap-6 sm:mt-4 sm:mb-6 xs:mt-4 xs:mb-6 xs:grid-cols-2"
		>
			<ItemCard
				v-for="item in products"
				:key="item.id"
				:item="item"
				@click="navigateToItemDetailsBound(item)"
			/>
		</div>
		<ShopGalleryPagination
			:total="productsTotal"
			@update:search="updatePage"
		/>
	</section>
</template>
